// reportItems.js
// Report items = one entry per jurisdiction form item number.
// Each item drives one photo slot in the photo report and one comment
// (findings) on the jurisdiction form.
//
// Built on top of jurisdictionSections.js (canonical section IDs and labels).
// Section labels carry the form item number ("3c.", "6a.", "17.") — sections
// that share an item number roll up into one item.
//
// Photo numbering:
//   PHOTO-01  Property Aerial View   (fixed, no findings box)
//   PHOTO-02  Building Elevation     (fixed, no findings box)
//   PHOTO-NN  Form item NN           (one primary photo + findings box)
//   Caption prints as "PHOTO-NN — <title>" using CAPTION_TITLES below.
//   PHOTO-NN-1, NN-2 …               (additional repair photos for item NN)

import { getSections } from './jurisdictionSections';

// ─── Fixed lead photos ────────────────────────────────────────────────────────

export const LEAD_ITEMS = [
  { key: 'aerial',    num: 1, photoLabel: 'PHOTO-01', title: 'Property Aerial View', findings: false },
  { key: 'elevation', num: 2, photoLabel: 'PHOTO-02', title: 'Building Elevation',   findings: false },
];

// ─── Grouped items ────────────────────────────────────────────────────────────
// When several sections share an item number, the form shows one comment box.
// commentKey is the formData key that holds that comment (matches
// ElectricalForm.js and the PDF fill in functions/index.js).

const GROUPS = {
  gutters_location: { title: 'Gutters',                  commentKey: 'gutters_comments' },
  branch_identified:{ title: 'Branch Circuits',          commentKey: 'branch_comments' },
  wiring_metallic:  { title: 'Types of Wiring Methods',  commentKey: 'wiring_methods_comments' },
  electric_service: { title: 'Electrical Service',       commentKey: 'electric_service' },   // Boca: condition + needs repair
};

// Item 7 — Electrical Panels (panel table + one comment). All jurisdictions.
const PANELS_ITEM = {
  num: 7,
  formRef: '7',
  title: 'Electrical Panels',
  sectionIds: ['electrical_panels'],
  commentKey: 'electrical_panels',
  commentKeys: ['electrical_panels'],
  naKey: null,
  // No item-level status: each panel in the panel table has its own
  // Good / Needs Repair (that is how the form is laid out).
  lines: [],
};

// Item 28 — Underground / Lower-Level Parking (Miami-Dade, City of Miami).
// The form has four lines (A, B1, B2, C), each with its own comment field.
const UNDERGROUND_ITEM = {
  num: 28,
  formRef: '28',
  title: 'Underground / Lower-Level Parking Garage',
  sectionIds: ['28_sump', '28_elevator', '28_elevator2', '28_gfci'],
  commentKey: null,
  commentKeys: ['28_sump', '28_elevator', '28_elevator2', '28_gfci'],
  naKey: '28_na',
  perLineComments: true,
  lines: [
    { id: '28_sump',      label: 'A. Sump Pumps Operational',                   opts: ['yes', 'repair', 'na'],            lbls: ['Yes', 'Needs Repair', 'N/A'] },
    { id: '28_elevator',  label: 'B1. Elevator Returns Above BFE',               opts: ['yes', 'no', 'repair', 'retrofit'], lbls: ['Yes', 'No', 'Needs Repair', 'Will Retrofit'] },
    { id: '28_elevator2', label: 'B2. Elevator Sensors (Flooded Hoistway)',      opts: ['yes', 'no', 'repair', 'retrofit'], lbls: ['Yes', 'No', 'Needs Repair', 'Will Retrofit'] },
    { id: '28_gfci',      label: 'C. GFCI Protection for Below Grade Circuits', opts: ['yes', 'no', 'repair', 'retrofit'], lbls: ['Yes', 'No', 'Needs Repair', 'Will Retrofit'] },
  ],
};

// Legacy photo tags that map to an item number rather than a section
const LEGACY_ITEM_TAGS = {
  underground_parking: 28,
};

// ─── Photo captions (engineer-approved wording, per jurisdiction) ────────────
// Keyed by form item number. Caption prints as "PHOTO-NN — <title>".

const CAPTION_TITLES = {
  broward: {
    3: 'Electric Service',
    4: 'Service Equipment',
    5: 'Electric Rooms',
    6: 'Gutters',
    7: 'Electrical Switchgear',
    8: 'Branch Circuits',
    9: 'Grounding of Service',
    10: 'Grounding of Equipment',
    11: 'Service Conduits/Raceways',
    12: 'Service Conductor and Cables',
    13: 'General Conduit/Raceways',
    14: 'Feeder Conductors',
    15: 'Busways',
    16: 'Other Conductors',
    17: 'Emergency Lighting',
    18: 'Building Egress Illumination',
    19: 'Fire Alarm System',
    20: 'Smoke Detectors',
    21: 'Exit Lights',
    22: 'Emergency Power Systems',
    23: 'Wiring and Conduit at Parking Lots and Garages',
    24: 'Swimming Pool Wiring',
    25: 'Wiring to Mechanical Equipment',
  },
  miami_dade: {
    3: 'Electric Service',
    4: 'Metering Equipment',
    5: 'Electric Rooms',
    6: 'Gutters',
    7: 'Electrical Panels',
    8: 'Branch Circuits',
    9: 'Grounding of Service',
    10: 'Branch Circuit Equipment Grounding',
    11: 'Service Conduit/Raceways',
    12: 'General Conduit/Raceways',
    13: 'Wire and Cables',
    14: 'Busways',
    15: 'Thermography Inspection Results',
    16: 'Other Conductors',
    17: 'Types of Wiring Methods',
    18: 'Emergency Lighting',
    19: 'Building Egress Illumination',
    20: 'Fire Alarm System',
    21: 'Smoke Detectors',
    22: 'Exit Signs',
    23: 'Emergency Generator',
    24: 'Wiring in Open or Undercover Parking Garage Areas',
    25: 'Open or Undercover Parking Garage and Egress Illumination',
    26: 'Swimming Pool Wiring',
    27: 'Wiring to Mechanical Equipment',
    28: 'Underground / Lower-Level Parking Garage',
  },
  city_of_miami: {
    3: 'Electric Service',
    4: 'Metering Equipment',
    5: 'Electric Rooms',
    6: 'Gutters',
    7: 'Electrical Panels',
    8: 'Branch Circuits',
    9: 'Grounding of Service',
    10: 'Branch Circuit Equipment Grounding',
    11: 'Service Conduit/Raceways',
    12: 'General Conduit/Raceways',
    13: 'Wire and Cables',
    14: 'Busways',
    15: 'Thermography Inspection Results',
    16: 'Other Conductors',
    17: 'Types of Wiring Methods',
    18: 'Emergency Lighting',
    19: 'Building Egress Illumination',
    20: 'Fire Alarm System',
    21: 'Smoke Detectors',
    22: 'Exit Signs',
    23: 'Emergency Generator',
    24: 'Wiring in Open or Undercover Parking Garage Areas',
    25: 'Open or Undercover Parking Garage and Egress Illumination',
    26: 'Swimming Pool Wiring',
    27: 'Wiring to Mechanical Equipment',
    28: 'Underground / Lower-Level Parking Garage',
  },
  boca_raton: {
    3: 'Electrical Service',
    4: 'Metering Equipment',
    5: 'Electrical Rooms',
    6: 'Gutters',
    7: 'Electrical Panels',
    8: 'Branch Circuits',
    9: 'Grounding of Service',
    10: 'Grounding of Equipment',
    11: 'Service Conduit/Raceways',
    12: 'Service Conductor and Cables',
    13: 'Types of Wiring Methods',
    14: 'Feeder Conductors',
    15: 'Emergency Lighting',
    16: 'Building Egress Illumination',
    17: 'Fire Alarm System',
    18: 'Smoke Detectors',
    19: 'Exit Signs',
    20: 'Emergency Generator',
    21: 'Wiring in Open or Undercover Parking Garage Areas',
    22: 'Open or Undercover Parking Garage and Egress Illumination',
    23: 'Wiring to Mechanical Equipment',
    24: 'Swimming Pool Wiring',
  },
};

const jurisdictionKey = (county) => {
  const c = (county || '').toLowerCase();
  if (c.includes('city of miami')) return 'city_of_miami';
  if (c.includes('miami'))         return 'miami_dade';
  if (c.includes('boca') || c.includes('boynton')) return 'boca_raton';
  return 'broward';
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const pad2 = (n) => String(n).padStart(2, '0');

// "3c. Electric Service — Condition" → { num: 3, ref: '3c', title: 'Electric Service: Condition' }
const parseLabel = (label) => {
  const m = (label || '').match(/^(\d+)([a-z]?)\.\s*(.*)$/i);
  if (!m) return null;
  const title = m[3].replace(/\s+—\s+/g, ': ').trim();
  return { num: parseInt(m[1], 10), ref: `${m[1]}${m[2].toLowerCase()}`, title };
};

const cleanLbl = (l) => (l || '').replace(/^✓\s*/, '');

// One status line per canonical section. Grouped items label each line by the
// part after the dash ("Location", "Taps and Fill", "Identified" …).
const lineFor = (section, grouped) => {
  const opts = section.opts || ['good', 'repair'];
  const lbls = (section.lbls || ['Good', 'Repairs Required']).map(cleanLbl);
  const label = grouped
    ? (section.parsed.title.split(': ').slice(1).join(': ') || section.parsed.title)
    : 'Condition';
  return { id: section.id, label, opts, lbls };
};

// ─── Item-specific detail fields ──────────────────────────────────────────────
// Stored in the slot (slot.details) and written to the form under the same keys
// the PDF fill already reads (svc_voltage, 28_levels, …).

const YES_NO = ['Yes', 'No'];

const detailFieldsFor = (item, county) => {
  const boca = jurisdictionKey(county) === 'boca_raton';
  if (item.num === 3) {
    return [
      { key: 'svc_voltage',  label: 'Voltage',               type: 'select', options: ['120/240V', '208/120V', '480/277V', 'Other'] },
      { key: 'svc_amps',     label: 'Service rating (A)',    type: 'text',   placeholder: 'e.g. 400 or Various' },
      { key: 'svc_phase',    label: 'Phase',                 type: 'select', options: ['Single Phase', 'Three Phase', 'Both'] },
      { key: 'svc_fuses',    label: 'Fuses',                 type: 'checkbox' },
      { key: 'svc_breakers', label: 'Breakers',              type: 'checkbox' },
    ];
  }
  if (item.num === 5 && boca) {
    return [
      { key: 'room_clear', label: 'Are all electrical rooms clear of any debris, storage or obstruction that would impede access to the electrical equipment?', type: 'select', options: YES_NO, required: true },
      { key: 'room_floor', label: 'What floor of the building is the electrical room located?', type: 'text', placeholder: 'e.g. Ground floor', required: true },
    ];
  }
  if (item.num === 7 && boca) {
    return [
      { key: 'panel_gfci_test',         label: 'Ground fault current inspection on MDP?', type: 'select', options: YES_NO },
      { key: 'panel_thermal_test',      label: 'Thermal imaging test conducted?',         type: 'select', options: YES_NO },
      { key: 'panel_thermal_anomalies', label: 'Anomalies in thermography report?',       type: 'select', options: YES_NO },
      { key: 'panel_location',          label: 'Location (one entry for all panels)',     type: 'text', placeholder: 'e.g. Electrical rooms', required: true },
    ];
  }
  if (item.sectionIds.includes('wiring_other')) {
    return [{ key: 'wiring_other_specify', label: 'Other conductors/cables — specify type', type: 'text', placeholder: 'e.g. Liquid-tight flexible metal conduit (LFMC)' }];
  }
  if (item.num === 28) {
    return [{ key: '28_levels', label: 'Number of levels below grade', type: 'text', placeholder: 'e.g. 2' }];
  }
  return [];
};

const usesUnderground = (county) => {
  const c = (county || '').toLowerCase();
  return c.includes('miami');   // Miami-Dade and City of Miami
};

// ─── Main builder ─────────────────────────────────────────────────────────────

export const getReportItems = (county) => {
  const sections = getSections(county).filter(s => s.id !== 'underground_parking');
  const byNum = new Map();

  sections.forEach(s => {
    const p = parseLabel(s.label);
    if (!p) return;
    if (!byNum.has(p.num)) byNum.set(p.num, []);
    byNum.get(p.num).push({ ...s, parsed: p });
  });

  const items = [];

  byNum.forEach((group, num) => {
    if (group.length === 1) {
      const s = group[0];
      items.push({
        num,
        formRef: s.parsed.ref,
        title: s.parsed.title,
        sectionIds: [s.id],
        commentKey: s.id,
        commentKeys: [s.id],
        naKey: null,
        lines: [lineFor(s, false)],
      });
    } else {
      const g = GROUPS[group[0].id] || { title: group[0].parsed.title.split(':')[0], commentKey: null };
      items.push({
        num,
        formRef: String(num),
        title: g.title,
        sectionIds: group.map(s => s.id),
        commentKey: g.commentKey,
        commentKeys: g.commentKey ? [g.commentKey] : group.map(s => s.id),
        naKey: null,
        lines: group.map(s => lineFor(s, true)),
      });
    }
  });

  if (!byNum.has(7)) items.push({ ...PANELS_ITEM });
  if (usesUnderground(county) && !byNum.has(28)) items.push({ ...UNDERGROUND_ITEM });

  return items
    .sort((a, b) => a.num - b.num)
    .map(it => {
      const title = CAPTION_TITLES[jurisdictionKey(county)][it.num] || it.title;
      // Item-specific fields that live in the slot (they fill the jurisdiction
      // form, not the photo report)
      const details = detailFieldsFor(it, county);

      // Thermography: when performed, the slot prints a "refer to report" panel
      // instead of a photo, with fixed findings text.
      const thermo = it.sectionIds.includes('thermography')
        ? { noPhoto: true, presetFindings: 'Refer to separate thermography report.' }
        : {};
      return {
        ...it,
        ...thermo,
        details,
        panelTable: it.num === 7,
        title,
        key: `item_${pad2(it.num)}`,
        photoLabel: `PHOTO-${pad2(it.num)}`,
        caption: title,
      };
    });
};

// Additional repair photo label for an item: PHOTO-27-1, PHOTO-27-2 …
export const extraPhotoLabel = (item, index) => `${item.photoLabel}-${index + 1}`;

// ─── Repair photos ────────────────────────────────────────────────────────────
// Repair photos (each slot's photos after the first) are numbered in sequence
// after the last form item, in item order: on a form ending at PHOTO-28 the
// first repair photo is PHOTO-29. Caption: "PHOTO-29 REPAIR ITEM 7".

export const repairCaption = (item) => `REPAIR ITEM ${item.formRef}`;

// Returns { item_07: ['PHOTO-29', 'PHOTO-30'], … } — one label per repair photo,
// in the order of slot.photos[1…]
export const numberRepairPhotos = (county, slots) => {
  const items = getReportItems(county);
  let next = Math.max(...items.map(i => i.num)) + 1;
  const labels = {};
  items.forEach(it => {
    const extras = (slots?.[it.key]?.photos || []).slice(1);
    labels[it.key] = extras.map(() => `PHOTO-${pad2(next++)}`);
  });
  return labels;
};

// Find the item a canonical section ID (or legacy tag) belongs to
export const itemForSection = (county, sectionId) => {
  const items = getReportItems(county);
  if (LEGACY_ITEM_TAGS[sectionId]) {
    return items.find(it => it.num === LEGACY_ITEM_TAGS[sectionId]) || null;
  }
  return items.find(it => it.sectionIds.includes(sectionId)) || null;
};

// ─── Site systems checklist ───────────────────────────────────────────────────
// Answered first in Photo Manager. "Not present" sets the item to N/A with the
// reason below (editable) and the AI skips it. Only systems that exist on the
// jurisdiction's form are asked.

const SITE_SYSTEM_DEFS = [
  { key: 'busways',      match: it => it.sectionIds.includes('busways'),         reason: 'no busways at this property' },
  { key: 'thermography', match: it => it.sectionIds.includes('thermography'),    reason: 'the service size did not warrant a thermographic inspection' },
  { key: 'fire_alarm',   match: it => it.sectionIds.includes('fire_alarm'),      reason: 'no fire alarm system at this property' },
  { key: 'generator',    match: it => it.sectionIds.includes('emergency_power'), reason: null },
  { key: 'pool',         match: it => it.sectionIds.includes('pool_wiring'),     reason: 'no swimming pool on site' },
  { key: 'underground',  match: it => it.num === 28,                             reason: 'no underground or lower-level parking garage at this property' },
];

export const getSiteSystems = (county) => {
  const items = getReportItems(county);
  const broward = jurisdictionKey(county) === 'broward';
  return SITE_SYSTEM_DEFS
    .map(def => {
      const item = items.find(def.match);
      if (!item) return null;
      const reason = def.key === 'generator'
        ? (broward ? 'no emergency power system at this property' : 'no emergency generator at this property')
        : def.reason;
      return { key: def.key, itemKey: item.key, photoLabel: item.photoLabel, title: item.title, defaultReason: reason };
    })
    .filter(Boolean);
};
